import React, { useEffect } from 'react';
import moment from 'moment';
import { useQuery } from 'react-query';
import { useForm } from 'antd/lib/form/Form';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form,
  Tabs,
  Input,
  DatePicker,
  notification,
  Col,
  Row,
  Collapse,
} from 'antd';
import { CaretRightOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import Value from 'components/Value';
import DF from 'DisplayFramework';
import {
  getPurchaseOrderByID,
  updatePurchaseOrder,
  createPurchaseOrder,
  PurchaseOrder,
} from 'services/purchaseOrder';
import { TpList } from 'services/thirdParty';
import FormInput from 'components/FormInput';
import SaveButton from 'components/SaveButton';
import ParListSelect from 'components/ParListSelect';
import { useModalState } from 'hooks/useModalState';
import TextArea from 'antd/lib/input/TextArea';
import PurOrderLinesTable from './PurOrderLinesTable';
import PropListModal from '../../ProposalV3/PropListModal';
import TpListModal from '../../ThirdParty/TpListModal';
import { getProposalById, ProposalListResponse } from 'services/proposal';
import Title from 'components/Title';
import TasksListPopover from 'pages/workflow/TasksListPopover';
import Button from 'components/Button';
import { toCurrency } from 'utils/utils';
import IActions from 'icons/IActions';
import ModalController from 'components/ModalController';
import ThirdPartyModal from 'pages/ThirdParty/ThirdPartyDetail/ThirdPartyModal';
import UtinaCard from 'components/UtinaCard';
import GetLabel from 'DisplayFramework/components/GetLabel';
import _ from 'lodash';
import Address from 'components/address';
import PrintingList from 'pages/Print/PrintingList/PrintingList';

const NEW_PURCHASE_ID = -1;

const { TabPane } = Tabs;

type PurchaseOrderDetailProps = {
  poid?: number;
};
const PurchaseOrderDetail = (props: PurchaseOrderDetailProps) => {
  const { poid } = props;

  const { idOrCreate } = useParams<{ idOrCreate: string }>();
  const isCreate = idOrCreate === 'create';
  const id = !isCreate ? idOrCreate : undefined;
  const {
    close: closePropList,
    // eslint-disable-next-line no-unused-vars
    open: openPropList,
    visible: visiblePropList,
  } = useModalState();
  const {
    close: closeTPList,
    // eslint-disable-next-line no-unused-vars
    open: openTPList,
    visible: visibleTPList,
  } = useModalState();

  const { data: purchaseOrders, refetch } = useQuery(
    `getPurchaseOrderByID`,
    () =>
      getPurchaseOrderByID({ poid: poid ? poid : Number(id) }).then((res) => {
        if (res.code === '403') {
          window.history.replaceState(null, '', '/403');
          window.location.reload();
        }
        return res.data;
      }),
    {
      enabled: !!id || !!poid,
      refetchOnWindowFocus: false,
    }
  );

  const tabPosition = localStorage.getItem('tabposition') ?? 'left';

  const [form] = useForm();
  const navigate = useNavigate();

  useEffect(() => {
    if (purchaseOrders) {
      form.setFieldsValue({
        ...purchaseOrders,
        podate: purchaseOrders.podate
          ? moment(purchaseOrders.podate, 'YYYY-MM-DD')
          : '',
        poexpecteddeliverydate: purchaseOrders.poexpecteddeliverydate
          ? moment(purchaseOrders.poexpecteddeliverydate, 'YYYY-MM-DD')
          : '',
        created_at: purchaseOrders.created_at,
        created_by: purchaseOrders.created_by,
        currcode: purchaseOrders.currcode,
        podescription: purchaseOrders.podescription,
        popaymentdelaylabel: purchaseOrders.popaymentdelaylabel,
        popaymentmodelabel: purchaseOrders.popaymentmodelabel,
        postatus: purchaseOrders.postatus,
        deliveryadrcity: purchaseOrders.deliveryadrcity,
        deliveryadrcountry: purchaseOrders.deliveryadrcountry,
        deliveryadrregion: purchaseOrders.deliveryadrregion,
        deliveryadrstreet1: purchaseOrders.deliveryadrstreet1,
        deliveryadrstreet2: purchaseOrders.deliveryadrstreet2,
        deliveryadrzipcode: purchaseOrders.deliveryadrzipcode,
        suppliername: purchaseOrders.suppliername,
        tpidsupplier: purchaseOrders.tpidsupplier,
        supplierreference: purchaseOrders.supplierreference,
      });
    }
  }, [purchaseOrders]);

  const submit = () => {
    const requestPaylod = {
      ...form.getFieldsValue(),
      podate: moment(form.getFieldValue('podate')).format('YYYY-MM-DD'),
      poexpecteddeliverydate: moment(
        form.getFieldValue('poexpecteddeliverydate')
      ).format('YYYY-MM-DD'),
      purOrderLines: form.getFieldValue('purOrderLines'),
      tpidsupplier: form.getFieldValue('tpidsupplier'),
    };

    if (isCreate) {
      return createPurchaseOrder({ poRequest: requestPaylod }).then(
        (response) => {
          if (response.code === '200') {
            notification.success({
              message: 'The Purchase Order has been successfully created',
            });
            navigate(`/purchase-order/${response.data.poid}`);
          } else {
            notification.error({
              message: response.message,
            });
          }
        }
      );
    } else {
      return updatePurchaseOrder({
        poid: Number(id),
        poRequest: requestPaylod,
      }).then((res) =>{
        if(res.code === '200'){
          notification.success({
            message: 'The Purchase Order has been successfully updated',
          })
        }
      }
      );
    }
  };

  const onProposalSelect = (selectedProposal: ProposalListResponse) => {
    getProposalById({ ctrid: selectedProposal.ctrid }).then((proposal) => {
      const { data } = proposal;
      let pogrossamount = 0;
      let potaxamount = 0;
      let ponetamount = 0;
      const proposalAssets = data.assets.map((asset) => {
        pogrossamount += asset.assetgrossamount;
        potaxamount += asset.assettaxamount;
        ponetamount += asset.assetnetamount;
        return {
          polreference: asset.assetid,
          taxamount: asset.assettaxamount,
          grossamount: asset.assetgrossamount,
          poltypelabel: 'Asset',
          objectlabel: asset.assetcategorylabel,
          unitprice: asset.assetnetamount,
          netafterdiscount: asset.assetnetamount,
          totalnetafterdiscount: asset.assetnetamount,
          qty: 1,
          polid: Math.round(_.random(100000) * 100),
          objectid: asset.assetid,
          taxcode: asset.taxcode,
          taxrate: asset.taxrate,
          statuslabel: asset.statuslabel,
          objecttype: 'ASSET',
          poltype: 'ASSET',
          discount: 0,
        };
      });
      form.setFieldsValue({
        ponetamount: Number(ponetamount).toFixed(2),
        potaxamount: Number(potaxamount).toFixed(2),
        pogrossamount: Number(pogrossamount).toFixed(2),
        suppliername: data.suppliername,
        supplierreference: data.supplierreference,
        tpidsupplier: data.tpidsupplier,
        deliveryadrstreet1: data.ctinvoiceaddressstr1,
        deliveryadrstreet2: data.ctinvoiceaddressstr2,
        deliveryadrzipcode: data.ctinvoiceaddresszipcode,
        deliveryadrcity: data.ctinvoiceaddresscity,
        deliveryadrregion: data.tpinvoiceaddressregion,
        deliveryadrcountry: data.ctinvoiceaddressstr1,
        purOrderLines: proposalAssets,
      });
    });
    closePropList();
  };

  const onTPSelect = (selectedTP: TpList) => {
    form.setFieldsValue({
      ...initialValues,
      tpidsupplier: selectedTP.tpid,
      deliveryadrcity: selectedTP.tpmainadrcity,
      deliveryadrstreet1: selectedTP.tpmainadrstreet1,
      supplierreference: selectedTP.tpreference,
      potype: selectedTP.tptype,
    });
    closeTPList();
  };

  const initialValues = {
    created_at: '',
    created_by: NEW_PURCHASE_ID,
    currcode: '',
    deliveryadrcity: '',
    deliveryadrcitylabel: '',
    deliveryadrcountry: '',
    deliveryadrregion: '',
    deliveryadrstreet1: '',
    deliveryadrstreet2: '',
    deliveryadrzipcode: '',
    mgid: Number(localStorage.getItem('mgid')),
    podate: '',
    podescription: '',
    poexpecteddeliverydate: '',
    poexternalref: '',
    pogrossamount: 0,
    poid: NEW_PURCHASE_ID,
    ponetamount: 0,
    popaymentdelay: '',
    popaymentmode: '',
    poreference: '',
    postatus: '',
    postatusdate: '',
    potaxamount: 0,
    potype: '',
    purOrderLines: [],
    tpidsupplier: NEW_PURCHASE_ID,
    updated_at: '',
    updated_by: NEW_PURCHASE_ID,
  };

  return (
    <DF.Provider pagcode="podetail">
      <Form
        form={form}
        onFinish={submit}
        initialValues={initialValues}
        layout="vertical" className='ut-custom-form'
      >
        <div className="card-container">
          <Row style={{ fontSize: '14px', fontWeight: 'bold' }}>
            <Col span={19}>
              <ArrowLeftOutlined
                onClick={() => {
                  navigate(`/purchase-order`);
                }}
                style={{
                  float: 'left',
                  margin: '0px 5px',
                  fontSize: 'larger',
                }}
              />
              {purchaseOrders ? (
                <>
                  <GetLabel label="PO" />
                  {purchaseOrders.poreference + ' - '}
                  <GetLabel label="Suppl" />
                  {purchaseOrders.supplierreference +
                    ' - ' +
                    purchaseOrders.suppliername}
                </>
              ) : (
                <Title level={4} comcode="NewPO"></Title>
              )}
            </Col>
            <Col span={5}>
              {tabPosition === 'left' ? (
                <Row>
                  <Col span={10}>
                    {Number(id) ? (
                      <TasksListPopover
                        entityId={Number(id)}
                        entityReference={purchaseOrders?.poreference}
                        entityName="PURORDER"
                        title={
                          purchaseOrders?.poreference +
                          ' - ' +
                          purchaseOrders?.suppliername
                        }
                      >
                        <Button comcode="actions" icon={<IActions />} />
                      </TasksListPopover>
                    ) : null}
                  </Col>
                  {!poid && (
                    <Col span={14}>
                      <SaveButton comcode="save" custom={{ height: 52 }} />
                    </Col>
                  )}
                </Row>
              ) : null}
            </Col>
          </Row>
          <Tabs
            defaultActiveKey="1"
            tabPosition="left"
            tabBarExtraContent={
              tabPosition === 'top' ?? (
                <div>
                  <TasksListPopover
                    entityId={Number(id)}
                    entityName="PURORDER"
                    entityReference={purchaseOrders?.poreference}
                    title={
                      purchaseOrders?.poreference +
                      ' - ' +
                      purchaseOrders?.suppliername
                    }
                  >
                    <Button comcode="actions" />
                  </TasksListPopover>
                  <SaveButton comcode="save" />
                </div>
              )
            }
          >
            <TabPane tab="General Data" key="1">
              <Collapse
                bordered={false}
                defaultActiveKey={['1']}
                expandIcon={({ isActive }) => (
                  <CaretRightOutlined rotate={isActive ? 90 : 0} />
                )}
                style={{ marginBottom: 24 }}
              >
                <div style={{ backgroundColor: '#f7f7f7', padding: 12 }}>
                  <Title comcode="generalinformation" level={5}></Title>
                  <div style={{ padding: 5, background: 'white' }}>
                    <div>
                      <Row>
                        <Col span={24}>
                          <Row gutter={16}>&nbsp;</Row>
                          <Row style={{ marginLeft: 50 }}>
                            <FormInput col xs={24} lg={4} name="poreference">
                              <Value />
                            </FormInput>
                            <FormInput col xs={24} lg={4} name="potypelabel">
                              <Value />
                            </FormInput>
                            <Form.Item noStyle shouldUpdate>
                              {({ getFieldValue }) => (
                                <FormInput
                                  name="statuslabel"
                                  style={
                                    getFieldValue('postatuslabel') ===
                                    'Validated'
                                      ? { color: 'green' }
                                      : { color: 'black' }
                                  }
                                  col
                                  xs={24}
                                  lg={4}
                                >
                                  <Text>
                                    {getFieldValue('postatuslabel')}{' '}
                                    {'(' +
                                      moment(
                                        getFieldValue('postatusdate')
                                      ).format('YYYY-MM-DD') +
                                      ')'}
                                  </Text>
                                </FormInput>
                              )}
                            </Form.Item>
                            <Col span={5}></Col>
                          </Row>
                          <Col span={24}>
                            <Row gutter={[50, 15]} style={{ marginLeft: 20 }}>
                              <UtinaCard
                                level={5}
                                comcode="purchaseOrderInfo"
                                style={{
                                  color: '#000',
                                  marginLeft: 20,
                                  paddingTop: 20,
                                }}
                                lg={7}
                              >
                                <Row gutter={16}>&nbsp;</Row>
                                <ModalController>
                                  {({ setVisible, visible }) => (
                                    <>
                                      <FormInput name="suppliername">
                                        <a
                                          onClick={() => {
                                            setVisible(true);
                                          }}
                                        >
                                          <div
                                            style={{
                                              width: '100%',
                                              padding: 5,
                                              paddingLeft: 10,
                                              border: 1,
                                              borderStyle: 'solid',
                                              borderColor: '#EAEAEA',
                                            }}
                                          >
                                            {purchaseOrders?.suppliername}
                                          </div>
                                        </a>
                                      </FormInput>

                                      <ThirdPartyModal
                                        tpId={form.getFieldValue(
                                          'tpidsupplier'
                                        )}
                                        visible={visible}
                                        onCancel={() => setVisible(false)}
                                        onSuccess={() => setVisible(false)}
                                        title={form.getFieldValue(
                                          'suppliername'
                                        )}
                                      />
                                    </>
                                  )}
                                </ModalController>{' '}
                                <Row>
                                  <Col span={12}>
                                    <FormInput
                                      col
                                      xs={24}
                                      lg={22}
                                      name="podate"
                                    >
                                      <DatePicker disabled />
                                    </FormInput>
                                  </Col>
                                  <Col span={12}>
                                    <FormInput
                                      col
                                      xs={24}
                                      lg={22}
                                      name="poexpecteddeliverydate"
                                    >
                                      <DatePicker inputReadOnly />
                                    </FormInput>
                                  </Col>
                                </Row>
                                <Row>
                                  <FormInput
                                    col
                                    xs={24}
                                    lg={12}
                                    name="poexternalref"
                                  >
                                    <Input />
                                  </FormInput>
                                </Row>
                                <Row>
                                  <FormInput name="podescription">
                                    <TextArea rows={4} cols={100} />
                                  </FormInput>
                                </Row>
                              </UtinaCard>
                              <UtinaCard
                                level={5}
                                comcode="DeliveryAdress"
                                style={{ color: '#000', marginLeft: 30 }}
                                lg={7}
                              >
                                <Row gutter={16}>&nbsp;</Row>

                                <Row gutter={[8, 8]}>
                                  <FormInput
                                    col
                                    xs={24}
                                    lg={24}
                                    name="deliveryadrstreet1"
                                  />
                                  <FormInput
                                    col
                                    xs={24}
                                    lg={24}
                                    name="deliveryadrstreet2"
                                  />
                                  <Address
                                    form={form}
                                    paysField="deliveryadrcountry"
                                    regionField="deliveryadrregion"
                                    villeField="deliveryadrcity"
                                    codePostalField="deliveryadrzipcode"
                                    colU={12}
                                    hideRegionGlobal
                                    Colw={
                                      localStorage.getItem('lancode') === 'FR'
                                        ? { span: 10 }
                                        : { span: 4 }
                                    }
                                    styles={{ marginBottom: '30px' }}
                                  />
                                </Row>
                              </UtinaCard>
                              <UtinaCard
                                level={5}
                                comcode="financialInformation"
                                style={{ color: '#000', marginLeft: 30 }}
                                lg={7}
                              >
                                <Col>
                                  <Row gutter={[8, 8]}>
                                    <br />
                                    <Row gutter={[8, 8]}>&nbsp;</Row>
                                    <Row>
                                      <FormInput
                                        col
                                        xs={24}
                                        style={{ color: 'red' }}
                                        lg={8}
                                        name="ponetamount"
                                      >
                                        <Value
                                          formatter={(value) =>
                                            toCurrency(
                                              value,
                                              form.getFieldValue('currcode')
                                            )
                                          }
                                        ></Value>
                                      </FormInput>
                                      <Col></Col>
                                      <FormInput
                                        col
                                        xs={24}
                                        style={{ color: 'red' }}
                                        lg={8}
                                        name="potaxamount"
                                      >
                                        <Value
                                          formatter={(value) =>
                                            toCurrency(
                                              value,
                                              form.getFieldValue('currcode')
                                            )
                                          }
                                        ></Value>
                                      </FormInput>
                                      <FormInput
                                        col
                                        xs={24}
                                        style={{ color: 'red' }}
                                        lg={8}
                                        name="pogrossamount"
                                      >
                                        <Value
                                          formatter={(value) =>
                                            toCurrency(
                                              value,
                                              form.getFieldValue('currcode')
                                            )
                                          }
                                        ></Value>
                                      </FormInput>
                                      {/* <FormInput col xs={24} lg={6} name="currcode" /> */}

                                      <FormInput
                                        col
                                        xs={18}
                                        lg={24}
                                        name="popaymentmode"
                                      >
                                        <ParListSelect parcode="PAYMODEOUT" />
                                      </FormInput>
                                      <FormInput
                                        col
                                        xs={18}
                                        lg={24}
                                        name="popaymentdelay"
                                      >
                                        <ParListSelect parcode="PAYDELAYOU" />
                                      </FormInput>
                                    </Row>
                                  </Row>
                                </Col>
                              </UtinaCard>
                            </Row>
                          </Col>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </Collapse>
            </TabPane>
            <TabPane tab="Detail" key="2">
              <Form.Item
                noStyle
                shouldUpdate={(
                  prevValue: PurchaseOrder,
                  nextValue: PurchaseOrder
                ) => {
                  return (
                    prevValue?.purOrderLines?.length !==
                    nextValue?.purOrderLines?.length
                  );
                }}
              >
                <PurOrderLinesTable form={form} refetch={refetch} />
              </Form.Item>
            </TabPane>
            <TabPane tab="Print" key="3">
              <PrintingList
                docmodule="PURORDER"
                poreference={purchaseOrders?.poreference}
              />
            </TabPane>
          </Tabs>
        </div>
      </Form>
      <PropListModal
        visible={visiblePropList}
        onCancel={closePropList}
        onSelect={onProposalSelect}
      />
      <TpListModal
        visible={visibleTPList}
        onCancel={closeTPList}
        onSelect={onTPSelect}
      />
    </DF.Provider>
  );
};

export default PurchaseOrderDetail;
